import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectCanAccess, selectUserMetadataLoadedInStore } from '../app/auth.js';
import { togglePanel, selectMultipleAnnotations, setDisplayedPhoto, setDisplayedPhotoForAnnotations } from '../app/interface.js';
import { projectsApi, setProjectId, selectProjectId } from '../app/projects.js';
import { layersApi } from '../app/layers.js';
import { make_generic_proxy_url } from '../app/utils.js';
import apiClient from '../app/apiClient.js';
import { setImageDimensions } from '../app/photoDimensionsCache.js';
import { setLayers } from '../app/layers.js';
import { replaceAnnotations, updateAnnotationState, clearCandidateAnnotations, selectAnnotations } from '../app/annotations.js';
import { replaceTextAnnotations } from '../app/textAnnotations.js';

import MapWrapper from '../Components/MapWrapper.jsx';
import PhotoViewerTools from '../Components/PhotoViewerTools.jsx';
import SideBar from '../Components/SideBar.jsx';
import MainSideBar from '../Components/MainSideBar.jsx';

import '../Components/MapWrapper.css';
import '../Components/SideBar.css';
import '../Components/FiltersAndLayers.css';

// Extracts the filename from a URL (last path segment, no query string).
function getFilenameFromUrl(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    const pathname = url.split('?')[0];
    const segment = pathname.replace(/\/+$/, '').split('/').pop();
    return segment || '';
  } catch {
    return '';
  }
}

const PRELOAD_RADIUS = 10; // preload this many images before and after current

function preloadImageUrl(url) {
  if (!url) return;
  const img = new Image();
  img.onload = () => {
    setImageDimensions(url, img.naturalWidth, img.naturalHeight);
  };
  img.src = url;
}

// Fetches photo layers for the given inspection and returns layers + flat list of all photos (with proxy URLs and display names).
function usePhotoViewerData(inspectionId) {
  const [photoLayers, setPhotoLayers] = useState([]); // [{ id, name, photos: [{ id, url, ... }] }]
  const [allImages, setAllImages] = useState([]);     // flat list { id, name, url, layerId, layerName }

  useEffect(() => {
    if (!inspectionId) {
      setPhotoLayers([]);
      setAllImages([]);
      return;
    }
    const authToken = apiClient.getAuthToken();
    if (!authToken) {
      setPhotoLayers([]);
      setAllImages([]);
      return;
    }
    layersApi.index(inspectionId, (layers) => {
      const photoOnly = (layers || []).filter((l) => l.type === 'photo');
      const layersWithPhotos = photoOnly.map((layer) => ({
        id: layer.id,
        name: layer.name,
        photos: (layer.photos || []).map((photo) => ({
          id: photo.id,
          url: make_generic_proxy_url(photo.url, 'file', authToken, { layerId: layer.id, photoId: photo.id }),
          name: getFilenameFromUrl(photo.url) || `Photo ${photo.id}`,
          layerId: layer.id,
          layerName: layer.name,
        })),
      }));
      setPhotoLayers(layersWithPhotos);
      setAllImages(layersWithPhotos.flatMap((l) => l.photos));
    });
  }, [inspectionId]);

  return { photoLayers, allImages };
}

// Full-screen photo viewer: one image at a time with layers filter, keyboard nav, and access check.
function PhotoViewer() {
  const [searchParams] = useSearchParams();
  const viewId = searchParams.get('viewId');
  const inspectionId = searchParams.get('inspectionId');
  const layerIdFromUrl = searchParams.get('layerId');
  const canAccess = useSelector(selectCanAccess);
  const userMetadataLoadedInStore = useSelector(selectUserMetadataLoadedInStore);
  const projectIdFromStore = useSelector(selectProjectId);
  const dispatch = useDispatch();
  const [projectIdLocal, setProjectIdLocal] = useState(null);
  const [selectedLayerId, setSelectedLayerId] = useState(layerIdFromUrl ?? null); // null = all layers
  const [currentIndex, setCurrentIndex] = useState(0);

  const { photoLayers, allImages } = usePhotoViewerData(inspectionId);
  const allAnnotations = useSelector(selectAnnotations);

  // Annotation count per photoId (used to show counts in the photo select).
  const annotationCountsByPhotoId = useMemo(() => {
    const counts = new Map();
    (allAnnotations || []).forEach((a) => {
      if (!a.photoId) return;
      const key = String(a.photoId);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [allAnnotations]);

  // When opening from Ortho with layerId in URL: keep it only if that layer exists; normalize type (URL string vs API number).
  useEffect(() => {
    if (!photoLayers.length || selectedLayerId == null) return;
    const layer = photoLayers.find((l) => String(l.id) === String(selectedLayerId));
    if (!layer) setSelectedLayerId(null);
    else if (layer.id !== selectedLayerId) setSelectedLayerId(layer.id);
  }, [photoLayers, selectedLayerId]);

  // Images to show: all or only the selected layer.
  const filteredImages = useMemo(() => {
    if (selectedLayerId == null || selectedLayerId === '') return allImages;
    return allImages.filter((img) => String(img.layerId) === String(selectedLayerId));
  }, [allImages, selectedLayerId]);

  const currentImage = filteredImages[currentIndex] ?? null;
  const preloadedIndicesRef = useRef(new Set());

  // Clear preloaded set when the list changes (e.g. layer filter or data refresh).
  useEffect(() => {
    preloadedIndicesRef.current.clear();
  }, [filteredImages]);

  // Preload images in a window around current index; only load those not already preloaded.
  useEffect(() => {
    if (!filteredImages.length) return;
    const start = Math.max(0, currentIndex - PRELOAD_RADIUS);
    const end = Math.min(filteredImages.length - 1, currentIndex + PRELOAD_RADIUS);
    const set = preloadedIndicesRef.current;
    for (let i = start; i <= end; i++) {
      if (set.has(i)) continue;
      preloadImageUrl(filteredImages[i]?.url);
      set.add(i);
    }
  }, [currentIndex, filteredImages]);

  // Reset to first image when the layer filter changes.
  useEffect(() => {
    setCurrentIndex(0);
  }, [selectedLayerId]);

  // Set default panel/annotation state on mount; on unmount clear layers, selection, annotations and displayed photo.
  // Photo viewer: only use annotate tab (analyse is disabled).
  useEffect(() => {
    dispatch(togglePanel({ id: 'annotate' }));
    dispatch(updateAnnotationState('DefaultForm'));
    return () => {
      dispatch(setLayers({ layers: [] }));
      dispatch(selectMultipleAnnotations([]));
      dispatch(updateAnnotationState('DefaultForm'));
      dispatch(clearCandidateAnnotations());
      dispatch(replaceAnnotations([]));
      dispatch(replaceTextAnnotations([]));
      dispatch(setDisplayedPhoto(null));
      dispatch(togglePanel({ id: 'annotate' }));
    };
  }, [dispatch]);

  // Defer Redux update so the map can paint the new image first (avoids extra MapWrapper re-render before paint).
  useEffect(() => {
    const img = currentImage ?? null;
    const id = requestAnimationFrame(() => {
      if (img) dispatch(setDisplayedPhoto(img));
      else dispatch(setDisplayedPhoto(null));
    });
    return () => cancelAnimationFrame(id);
  }, [currentImage, dispatch]);

  // For map annotations, use a slightly delayed photo so heavy filtering/features update runs after the image swap.
  useEffect(() => {
    const img = currentImage ?? null;
    const timeoutId = setTimeout(() => {
      if (img) dispatch(setDisplayedPhotoForAnnotations(img));
      else dispatch(setDisplayedPhotoForAnnotations(null));
    }, 120); // small delay so image swap feels instant, annotations catch up just after
    return () => clearTimeout(timeoutId);
  }, [currentImage, dispatch]);

  // Arrow keys to go to previous/next image (ignored when focus is in input/textarea/select).
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      if (filteredImages.length <= 1) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentIndex((i) => (i - 1 + filteredImages.length) % filteredImages.length);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentIndex((i) => (i + 1) % filteredImages.length);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredImages.length]);

  // Fetch project for this inspection and set projectId (for access check and store).
  useEffect(() => {
    if (!inspectionId) return;
    projectsApi.get({ inspectionId }, 'id', ({ project }) => {
      if (project?.id) {
        setProjectIdLocal(project.id);
        dispatch(setProjectId(project.id));
      }
    });
  }, [inspectionId, dispatch]);

  const projectId = projectIdLocal ?? projectIdFromStore;

  if (
    userMetadataLoadedInStore &&
    projectIdLocal &&
    inspectionId &&
    viewId &&
    !canAccess('PhotoViewer', { projectId: projectIdLocal, inspectionId, viewId })
  ) {
    return <Navigate to="/" replace />;
  }

  if (!inspectionId || allImages.length === 0) {
    return (
      <div className="full-screen">
        <MainSideBar />
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', zIndex: 1 }}>
          <Typography>{inspectionId ? 'Loading images…' : 'No inspection.'}</Typography>
        </Box>
        <SideBar />
      </div>
    );
  }

  return (
    <div className="full-screen">
      <MainSideBar />
      <MapWrapper
        mode="photo"
        singleImageUrl={currentImage?.url}
        photoId={currentImage?.id}
      />
      <PhotoViewerTools
        photoLayers={photoLayers}
        selectedLayerId={selectedLayerId}
        onLayerChange={setSelectedLayerId}
        images={filteredImages}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        annotationCountsByPhotoId={annotationCountsByPhotoId}
      />
      <SideBar />
    </div>
  );
}

export default PhotoViewer;
